import { DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useCancelSubscription, useDeactivateAccount } from '../../hooks/useAccountMutations';
import { completeSignOut } from '../../hooks/useAuth';
import { discardLocalSession } from '../../services/authentication';
import { patchAuthFlow } from '../../state/app-state';
import { Account } from '../../services/models/account';
import CancelSubscriptionDialog from '../cancel-subscription-dialog/CancelSubscriptionDialog';
import { Modal } from '../ui';

const DEACTIVATION_REASONS = [
  { id: 'duplicate-account', label: 'I have a duplicate account' },
  { id: 'too-many-emails', label: 'I am getting too many emails' },
  { id: 'not-useful', label: "I don't find Newslit useful" },
  { id: 'privacy', label: 'I have a privacy concern' },
  { id: 'dont-like-reading-news', label: "I don't like reading news" },
  { id: 'stories-not-interesting', label: 'The stories were not interesting to me' },
  { id: 'how-to-use', label: "I can't figure out how to use Newslit" },
  { id: 'too-much-email', label: 'I get too much email in general' },
  { id: 'other', label: 'Other' },
];

interface DeactivateAccountDialogProps {
  account: Account;
  open: boolean;
  onClose: () => void;
}

const DeactivateAccountDialog: FunctionComponent<DeactivateAccountDialogProps> = ({
  account,
  open,
  onClose,
}) => {
  const history = useHistory();

  const { cancelSubscription, isCancelingSubscription } = useCancelSubscription();
  const { deactivateAccount, isDeactivatingAccount } = useDeactivateAccount();

  const subscription = account.subscription;
  const hasActiveSubscription = !!(subscription && !subscription.trial && !subscription.canceled);

  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [comment, setComment] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [showCancelSubsDialog, setShowCancelSubsDialog] = useState(false);

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const success = await deactivateAccount(reason, comment);
    if (success) {
      // Same teardown the legacy /cancel page performed. The notice flag
      // must be set after completeSignOut (which resets the auth flow) and
      // travels through the auth-flow state, not route state — losing the
      // session makes ProtectedRoute race this navigation with its own
      // sign-in redirect, and whichever wins must still show the notice.
      discardLocalSession();
      completeSignOut();
      patchAuthFlow({ isAccountDeactivated: true });
      history.replace('/users/signin');
    }
  };

  return (
    <Modal isOpen={open} onClose={() => onClose()}>
      <form noValidate onSubmit={onSubmit}>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <DialogTitle as="h3" className="text-lg leading-6 font-medium text-gray-900">
              Deactivate Account
            </DialogTitle>
            <div className="mt-2">
              <p className="text-sm text-gray-500 m-0">
                Are you sure you want to deactivate your Newslit account?
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-500 space-y-2">
          <p className="m-0">
            You should know you can{' '}
            <Link
              to="/dashboard/settings"
              className="font-medium text-secondary-dark hover:text-secondary"
              onClick={() => onClose()}
            >
              change or disable
            </Link>{' '}
            the emails and notifications you receive from Newslit instead.
          </p>
          <p className="m-0">If you deactivate your Newslit account:</p>
          <ul className="list-disc pl-5 m-0">
            <li>You will no longer receive daily News Briefs</li>
            <li>You may miss important news stories</li>
          </ul>
        </div>

        {hasActiveSubscription && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-700 m-0">
              You have an active subscription. Please, cancel your subscription before deactivating
              your account.
            </p>
            <button
              type="button"
              className="mt-3 inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              onClick={() => setShowCancelSubsDialog(true)}
            >
              Cancel Subscription
            </button>
          </div>
        )}

        <fieldset className="mt-4" disabled={hasActiveSubscription}>
          <legend className="text-sm font-medium text-gray-900">Reason for deactivating:</legend>
          <div className="mt-2 space-y-2">
            {DEACTIVATION_REASONS.map(({ id, label }) => (
              <div key={id} className="flex items-center">
                <input
                  id={id}
                  name="reason"
                  type="radio"
                  value={label}
                  checked={reason === label}
                  disabled={hasActiveSubscription}
                  className="h-4 w-4 text-secondary border-gray-300 focus:ring-secondary disabled:opacity-50"
                  onChange={event => {
                    setReasonTouched(true);
                    setReason(event.currentTarget.value);
                  }}
                />
                <label htmlFor={id} className="ml-3 block text-sm text-gray-700">
                  {label}
                </label>
              </div>
            ))}
          </div>
          {!reason && reasonTouched && (
            <p className="mt-2 text-sm text-red-600 m-0">Please, select a reason.</p>
          )}
        </fieldset>

        <div className="mt-4">
          <textarea
            rows={3}
            placeholder="Any comment for us?"
            value={comment}
            disabled={hasActiveSubscription}
            className="shadow-xs block w-full focus:ring-secondary focus:border-secondary sm:text-sm border border-gray-300 rounded-md disabled:opacity-50"
            onChange={event => setComment(event.target.value)}
          />
        </div>

        <div className="mt-4 flex items-center">
          <input
            id="confirm-deactivation"
            type="checkbox"
            checked={confirmation}
            disabled={hasActiveSubscription}
            className="h-4 w-4 text-secondary border-gray-300 rounded-sm focus:ring-secondary disabled:opacity-50"
            onChange={event => {
              setReasonTouched(true);
              setConfirmation(event.target.checked);
            }}
          />
          <label htmlFor="confirm-deactivation" className="ml-3 block text-sm text-gray-700">
            Yes, really deactivate my Newslit account
          </label>
        </div>

        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="submit"
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
            disabled={
              hasActiveSubscription ||
              isDeactivatingAccount ||
              !confirmation ||
              !reasonTouched ||
              !reason
            }
          >
            {isDeactivatingAccount && (
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isDeactivatingAccount ? 'Deactivating...' : 'Deactivate'}
          </button>
          <button
            type="button"
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:w-auto sm:text-sm"
            disabled={isDeactivatingAccount}
            onClick={() => onClose()}
          >
            Cancel
          </button>
        </div>
      </form>

      <CancelSubscriptionDialog
        account={account}
        open={showCancelSubsDialog}
        isCanceling={isCancelingSubscription}
        onAccept={async () => {
          // Keep the dialog open on failure so the user can retry; the
          // error itself surfaces via the global ErrorReporter.
          const canceled = await cancelSubscription();
          if (canceled) {
            setShowCancelSubsDialog(false);
          }
        }}
        onCancel={() => setShowCancelSubsDialog(false)}
      />
    </Modal>
  );
};

export default DeactivateAccountDialog;
